/**
 * CaseMatcherLink — a compact case-level entry point into the line-item Item Matcher
 * (`/matcher/:reportId`). Matching is a "walks the lines" function — adjuster /
 * contractor / data_specialist (+ owner) — NOT accountant-only (owner directive
 * 2026-09-16), so this chip is placed on those case surfaces next to reconciliation.
 *
 * Neutral action chip with a single brand-colored icon — the sanctioned grammar
 * (orange as a thin accent, action text hueless), matching the accountant workbench's
 * Engine Review / Export Center chips.
 */
import { Link } from 'react-router-dom';
import { GitCompare, Scale } from 'lucide-react';

export function CaseMatcherLink({
  reportId,
  className = '',
}: {
  reportId?: string | null;
  className?: string;
}) {
  if (!reportId) return null;
  return (
    <Link
      to={`/matcher/${reportId}`}
      data-testid="case-item-matcher-link"
      title="Open the line-item Item Matcher for this case"
      className={`cs-chip inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-surface px-3 py-2 text-xs font-medium text-text-secondary shadow-sm transition-colors hover:bg-hover hover:text-text-primary ${className}`}
    >
      <GitCompare className="h-3.5 w-3.5" style={{ color: 'var(--color-brand)' }} />
      Item Matcher
    </Link>
  );
}

/**
 * CaseReconcileLink — case-level entry into the two-sided manual reconciler
 * (`/reconcile/:reportId`): adjuster vs contractor, free N:M linking + No-Match with
 * carry-forward. Same "walks the lines" audience + chip grammar as CaseMatcherLink;
 * placed beside it on the adjuster / contractor / data_specialist (+ owner) surfaces.
 */
export function CaseReconcileLink({
  reportId,
  className = '',
}: {
  reportId?: string | null;
  className?: string;
}) {
  if (!reportId) return null;
  return (
    <Link
      to={`/reconcile/${reportId}`}
      data-testid="case-reconcile-link"
      title="Reconcile the adjuster and contractor estimates for this case"
      className={`cs-chip inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-surface px-3 py-2 text-xs font-medium text-text-secondary shadow-sm transition-colors hover:bg-hover hover:text-text-primary ${className}`}
    >
      <Scale className="h-3.5 w-3.5" style={{ color: 'var(--color-brand)' }} />
      Reconcile Estimates
    </Link>
  );
}

export default CaseMatcherLink;
